import { getLogin } from "api/authAPI";
import { useAuth } from "components/Auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginPresenter } from "./LoginPresenter";
import {
  GetLoginResponse,
  LoginResponse,
  PostLoginResponse,
} from "api/interfaces/authInterface.interface";
import { onSuccessLogin, setLoginToken } from "utils/AuthUtil";
import useStatusMutation from "hook/useStatusMutation";
import qs from "qs";
import { ValidAuth } from "redux/reducers/auth/isAuth";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useMenu } from "hook/useMenu";
import { useFlag } from "@unleash/proxy-client-react";
import useApiError from "hook/useApiError";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import useRegisterPasskey from "hook/useRegisterPasskey";
import { checkPasskeySupport, getInitialLoginEmail } from "utils/functions";
import { getCookie, setCookie } from "cookies/cookie";

export enum LOGIN_TAB {
  EMAIL = "email",
  PASSKEY = "passkey",
  DW_CLOUD = "dw_cloud",
  RESELLER = "reseller",
}
export enum LOGIN_STEP {
  INPUT_EMAIL = "INPUT_EMAIL",
  INPUT_PASSWORD = "INPUT_PASSWORD",
  FORGOT_EMAIL = "FORGOT_EMAIL",
  FORGOT_EMAIL_SENT = "FORGOT_EMAIL_SENT",
  FORGOT_PASSWORD = "FORGOT_PASSWORD",
  FREE_TRIAL = "FREE_TRIAL",
  SETUP_PASSKEY = "SETUP_PASSKEY",
  LOGIN_WITH_PASSKEY = "LOGIN_WITH_PASSKEY",
  DW_CLOUD = "DW_CLOUD",
  RESELLER = "RESELLER",
}

export interface LoginInfo {
  email: string;
  password: string;
  tel?: string;
}

export interface FreeTrialInfo {
  email: string;
  code: string;
  organizationName: string;
}

export function LoginPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState<string>("");
  const isFromCodeLogin = useRef<boolean>(false);
  const isPasskeyEnabled = useFlag("feature_login_passkey");
  const [step, setSteps] = useState<LOGIN_STEP>(LOGIN_STEP.INPUT_EMAIL);
  const [loginInfo, setLoginInfo] = useState<LoginInfo>(() => ({
    email: getInitialLoginEmail(location),
    password: "",
    tel: "",
  }));
  const [hideOffer, setHideOffer] = useState<boolean>(
    () => getCookie("hideOffer") === "true"
  );
  const loginTab = useMemo(() => {
    if (
      step === LOGIN_STEP.SETUP_PASSKEY ||
      step === LOGIN_STEP.LOGIN_WITH_PASSKEY
    ) {
      return LOGIN_TAB.PASSKEY;
    }
    if (step === LOGIN_STEP.DW_CLOUD) {
      return LOGIN_TAB.DW_CLOUD;
    }
    return LOGIN_TAB.EMAIL;
  }, [step]);

  const [isValidated, setIsValidated] = useState<Validate>({
    email: false,
    password: false,
    tel: false,
  });

  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    email: "",
    password: "",
    tel: "",
  });

  const [cloudLoading, setCloudLoading] = useState<boolean>(false);

  const qsSearchParam = qs.parse(location?.search, {
    ignoreQueryPrefix: true,
    decoder: (s) => decodeURIComponent(s),
  });
  const email =
    qsSearchParam.email !== null ? (qsSearchParam.email as string) : "";
  const toSearchParam = new URLSearchParams(location.state?.search);
  //const email = toSearchParam.get("email") !== null ? toSearchParam.get("email") as string : "";
  const mobilePlusCode = qsSearchParam.mobilePlusCode;
  const { returnToStartPage } = useMenu({});
  const { displayError, intlString, lang, notifySuccess } = useApiError();
  const [suppertedOS, setSuppertedOS] = useState<boolean>(false);
  const { onRegisterPasskey, setLoginData } = useRegisterPasskey({
    onSuccess: () => {
      setCloudLoading(false);
      notifySuccess(intlString(lang.label.passkey.passkeySetupSuccess));
      setSteps(LOGIN_STEP.LOGIN_WITH_PASSKEY);
    },
    onFail: () => {
      setCloudLoading(false);
    },
  });

  const { isLogout, isAuthenticated }: ValidAuth = useAppSelector(
    (state) => state.isAuth
  );

  const onError = (err?: CustomError) => {
    setCloudLoading(false);
    setIsValidated({ ...isValidated, password: true });
    setValidateMsg({
      ...validateMsg,
      password: displayError(
        err as CustomError,
        intlString(lang.validateMsg.signup.loginIncorrect)
      ),
    });
  };
  const onLoginSuccess = (
    data: GetLoginResponse | PostLoginResponse,
    validated?: boolean
  ) => {
    const result: LoginResponse = data.result;

    const searchParams = location.state?.search
      ? new URLSearchParams(location.state?.search)
      : new URLSearchParams(location.search);
    let accountId = result.accountId;
    if (
      !accountId &&
      searchParams.get("accountId") &&
      searchParams.get("accountId") !== "undefined"
    ) {
      accountId = searchParams.get("accountId") as string;
    }
    if (
      !accountId &&
      searchParams.get("account") &&
      searchParams.get("account") !== "undefined"
    ) {
      accountId = searchParams.get("account") as string;
    }

    if (result.phoneNumber) {
      setLoginInfo((info) => ({
        ...info,
        tel: result.phoneNumber,
      }));
    }

    // 2fa 인증필요 && 30일 미인증 -> 2fa 페이지 이동
    if (
      result &&
      result.requiredTwoFactor &&
      step !== LOGIN_STEP.LOGIN_WITH_PASSKEY &&
      !validated
    ) {
      setLoginToken({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        userKey: result.userKey,
        email: loginInfo.email,
        memberKey: result.memberKey,
        hiddenKey: result.hiddenKey,
      });
      navigate(
        {
          pathname: `/verify/2fa`,
          search: `?account=${accountId}&r=${result.memberKey}`,
        },
        {
          state: {
            isAuth: true,
            verifyType: result.use2faCode,
            loginInfo: {
              email: loginInfo.email,
              password: loginInfo.password,
              phone: result.phoneNumber,
              userKey: result.userKey,
              memberKey: result.memberKey,
              hiddenKey: result.hiddenKey,
            },
            isLogin: result,
            setupPasskey: step === LOGIN_STEP.SETUP_PASSKEY,
            accountId: accountId,
            search: location.state?.search ?? location.search,
          },
          replace: true,
        }
      );
      return;
    }
    if (step === LOGIN_STEP.SETUP_PASSKEY) {
      setLoginData(data);
      onRegisterPasskey(loginInfo.email);
      setCloudLoading(true);
      return;
    }
    const email = loginInfo.email ?? result?.email;
    setCloudLoading(true);
    onSuccessLogin(auth, email, result, onError, toSearchParam);
  };

  const mutationLoginFromCode = useStatusMutation(getLogin, {
    onSuccess: (data: GetLoginResponse) => {
      onLoginSuccess(data);
    },
    onError: (e: CustomError) => {
      setCloudLoading(false);
      onError(e);
    },
  });

  const onChangeStep = useCallback((step: LOGIN_STEP) => {
    if (step === LOGIN_STEP.FREE_TRIAL) {
      navigate("/login/free-trial");
      return;
    }
    setIsValidated({
      email: false,
      password: false,
      tel: false,
    });
    setValidateMsg({
      email: "",
      password: "",
      tel: "",
    });
    setSteps(step);
  }, []);

  useEffect(() => {
    if (!location.search) return;
    if (!!mobilePlusCode) {
      return;
    }
    if (location.pathname === "/login/free-trial") {
      setSteps(LOGIN_STEP.FREE_TRIAL);
    } else if (location.pathname === "/login/setup-passkey") {
      setSteps(LOGIN_STEP.SETUP_PASSKEY);
    } else if (location?.state?.step === LOGIN_STEP.LOGIN_WITH_PASSKEY) {
      setSteps(LOGIN_STEP.LOGIN_WITH_PASSKEY);
    } else {
      setSteps(LOGIN_STEP.INPUT_EMAIL);
    }
  }, [location, mobilePlusCode]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchCode = searchParams.get("code");

    if (searchCode !== undefined && searchCode !== null) {
      setCode(searchCode);
      window.opener.postMessage({ code: searchCode }, window.location.origin);
    }
    (async () => {
      const hasCode = code !== undefined && code !== "";
      if (!mutationLoginFromCode.idle) return;
      if (hasCode) {
        window.opener.postMessage({ code }, window.location.origin);
        return;
      }

      const hasLoginInfoFromMobilePlus = !!mobilePlusCode && !!email;
      if (hasLoginInfoFromMobilePlus && !isFromCodeLogin.current) {
        isFromCodeLogin.current = true;
        mutationLoginFromCode.mutate({
          code: mobilePlusCode,
        });
      }
    })();
  }, [location]);

  const onChangeOfferDisplay = () => {
    setCookie({
      name: "hideOffer",
      value: (!hideOffer).toString(),
      options: {
        path: "/",
        expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
      },
    });
    setHideOffer(!hideOffer);
  };

  useEffect(() => {
    //세션만료가 아니고 로그아웃도 아닌 상태(로그인) 첫페이지로 이동
    if (!!mobilePlusCode) {
      return;
    }
    if (!isLogout && !isAuthenticated && auth.user.userId && !mobilePlusCode) {
      returnToStartPage();
    }
  }, [
    isLogout,
    isAuthenticated,
    location.pathname,
    auth.user.userId,
    returnToStartPage,
  ]);
  useEffect(() => {
    checkPasskeySupport().then((isSupported) => {
      setSuppertedOS(isSupported);
    });
  }, []);
  useEffect(() => {
    if (location.state?.userType) {
      setSteps(LOGIN_STEP.RESELLER);
      setLoginInfo((info) => ({
        ...info,
        email: location.state.email as string,
      }));
    }
  }, [location.state?.userType]);
  return (
    <LoginPresenter
      step={step}
      loginInfo={loginInfo}
      isValidated={isValidated}
      validateMsg={validateMsg}
      onChangeStep={onChangeStep}
      cloudLoading={cloudLoading}
      setLoginInfo={setLoginInfo}
      setIsValidated={setIsValidated}
      setValidateMsg={setValidateMsg}
      setCloudLoading={setCloudLoading}
      onLoginSuccess={onLoginSuccess}
      isPasskeyEnabled={isPasskeyEnabled && suppertedOS}
      loginTab={loginTab}
      hideOffer={hideOffer}
      onChangeOfferDisplay={onChangeOfferDisplay}
    />
  );
}
